import { FormHelperText, Stack, Typography, Box, Paper } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import {
  ApiRequest_Create_PropertyLocation,
  useCreatePropertyLocation,
} from '@/api/endpoints/propertyLocation/useCreatePropertyLocation';
import { PropertyLocationType } from '@/api/enums';
import { PMPRoutes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { useOnboardingProgress } from '@/context/OnboardingProgressProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import FormAddressAutocomplete from '@/components/form/FormAddressAutocomplete';
import FormSelect from '@/components/form/FormSelect';
import FormTextField from '@/components/form/FormTextField';
import MapboxAddressMap from '@/components/mapbox/MapboxAddressMap';
import {
  propertyLocationDeclaredUnitsCountOptions,
  propertyLocationTypeOptions,
} from '@/components/propertyLocation/options';

const PropertyLocationCreatePage: React.FC = () => {
  const { refetch, setSelectedPropertyLocationId } = usePropertyManager();
  const { refetch: refetchOnboardingProgress } = useOnboardingProgress();
  const { user } = useAuth();
  const history = useHistory();

  const isPropertyOwner = user?.user_metadata.campaign === 'property_owner';

  const defaultValues = useMemo(
    () => ({
      name: '',
      address_text: '',
      type: isPropertyOwner ? PropertyLocationType.OTHER : undefined,
      declared_units_count: undefined,
      metadata: isPropertyOwner ? { usage: '' } : undefined,
    }),
    [isPropertyOwner]
  );

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ApiRequest_Create_PropertyLocation>({
    defaultValues,
    mode: 'onBlur',
  });

  const [formError, setFormError] = useState<Error | null>(null);

  const addressText = watch('address_text');
  const propertyLocationType = watch('type');

  const createPropertyLocationMutation = useCreatePropertyLocation({
    onSuccess: async data => {
      // Refresh property manager context and onboarding progress
      await Promise.all([refetch(), refetchOnboardingProgress()]);

      // Set the newly created location as selected in the sidebar
      setSelectedPropertyLocationId(data.id);
      enqueueSnackbar(`Successfully added ${data.name}`, { variant: 'success' });

      // Redirect to dashboard
      history.push(PMPRoutes.dashboard);
    },
    onError: error => {
      setFormError((error?.response?.data ?? error) as Error);
    },
  });

  const onSubmit = useCallback(
    (data: ApiRequest_Create_PropertyLocation) => createPropertyLocationMutation.mutateAsync(data),
    [createPropertyLocationMutation]
  );

  const emailPreview = useMemo(() => {
    if (!addressText) return null;

    // Extract state from address (this is a simplified version)
    const stateMatch = addressText.match(/,\s*([A-Z]{2})/);
    const state = stateMatch ? stateMatch[1] : '';

    // Extract city from address
    const cityMatch = addressText.match(/,\s*([^,]+),\s*[A-Z]{2}/);
    const city = cityMatch ? cityMatch[1].trim() : '';

    // Generate sample name (remove spaces and special characters)
    // Just use the first part of the address (street number and name)
    const streetPart = addressText.split(',')[0]?.trim();
    const formattedName = streetPart?.replace(/[^a-zA-Z0-9]/g, '') || 'property';

    // Always provide at least one fallback option
    const options = [
      `${formattedName}@mail.terr.ai`,
      state ? `${formattedName}.${state}@mail.terr.ai` : null,
      city ? `${formattedName}.${city}@mail.terr.ai` : null,
    ].filter(Boolean);

    return options[0]?.toLowerCase() || null; // Always return at least the first option
  }, [addressText]);

  return (
    <MuiPageWrapper>
      <Box display="flex" height="100vh">
        <Box flex={1} p={3} sx={{ overflowY: 'auto', backgroundColor: 'background.paper' }}>
          <Stack spacing={3} maxWidth="35rem" mx="auto">
            <DashboardHeader
              breadcrumbs={[{ label: 'Locations', to: PMPRoutes.propertyLocationList }]}
              title="Add Property Location"
            />

            <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={3} width="100%">
              <Stack spacing={3.5} mb={1}>
                <Typography variant="body2">
                  {isPropertyOwner
                    ? "Whether it's your personal home, a rental unit, or a commercial space — adding your property helps us organize everything in one place. From smart document storage to automated reminders, Terr.ai works behind the scenes to keep you in control."
                    : 'This is the location that you are renting. A unique email address for this location will be generated from the rental property address you enter. Your tenants will use this email to contact you.'}
                </Typography>
              </Stack>

              <Stack spacing={2.5}>
                <FormTextField
                  label="Enter a name to help you identify this property (e.g Florida Beach House)"
                  placeholder="Enter property name"
                  error={!!errors.name}
                  helperText={errors.name?.message?.toString()}
                  required
                  {...register('name', {
                    required: 'Property Name is required',
                  })}
                  control={control}
                />

                <FormAddressAutocomplete
                  label={
                    isPropertyOwner
                      ? 'Address of the property (e.g. 123 Main St, Miami, FL 33130)'
                      : 'Address for the property you are renting (e.g. 123 Main St, Miami, FL 33130)'
                  }
                  placeholder="Enter property rental address"
                  error={!!errors.address_text}
                  helperText={errors.address_text?.message?.toString()}
                  required
                  {...register('address_text', {
                    required: 'Property Rental Address is required',
                  })}
                  control={control}
                />

                {emailPreview && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        borderColor: 'divider',
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Email Preview:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                        {emailPreview}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        This is an example of the email address that will be generated for this
                        property which you will use to communicate with your tenants as well as
                        vendors.
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {isPropertyOwner && (
                  <FormTextField
                    label="How do you use this property?"
                    placeholder="e.g., I live here or its my primary residence, This is my dorm, I am a paying guest, I rent this space, etc."
                    tooltipText="We need this information to offer personalized responses based on how you use the property."
                    error={!!errors.metadata?.usage}
                    helperText={errors.metadata?.usage?.message?.toString()}
                    {...register('metadata.usage')}
                    control={control}
                    multiline
                    rows={2}
                  />
                )}

                {!isPropertyOwner && (
                  <FormSelect
                    label="Property Type"
                    placeholder="Select property type"
                    error={!!errors.type}
                    helperText={errors.type?.message?.toString()}
                    required={!isPropertyOwner}
                    {...register('type', {
                      required: !isPropertyOwner && 'Property Type is required',
                    })}
                    control={control}
                    options={propertyLocationTypeOptions}
                  />
                )}

                {propertyLocationType === PropertyLocationType.MULTI_UNIT && (
                  <FormSelect
                    label="Units Count"
                    placeholder="Select units count"
                    error={!!errors.declared_units_count}
                    helperText={errors.declared_units_count?.message?.toString()}
                    required
                    {...register('declared_units_count', {
                      required: 'Units Count is required',
                    })}
                    control={control}
                    options={propertyLocationDeclaredUnitsCountOptions}
                  />
                )}

                {!!formError && <FormHelperText error>{formError.message}</FormHelperText>}
              </Stack>

              <Stack spacing={1} mb={1}>
                <LoadingButton loading={isSubmitting} fullWidth variant="contained" type="submit">
                  Create Property Location
                </LoadingButton>
                <LoadingButton
                  variant="outlined"
                  fullWidth
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Navigate to dashboard instead of history.goBack()
                    history.push(PMPRoutes.dashboard);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </LoadingButton>
              </Stack>
            </Stack>
          </Stack>
        </Box>

        <Box flex={1} sx={{ display: { xs: 'none', md: 'block' } }}>
          <MapboxAddressMap addressText={addressText} />
        </Box>
      </Box>
    </MuiPageWrapper>
  );
};

export default PropertyLocationCreatePage;
